import React, {useEffect, useRef, useState} from 'react';
import '../payment.css';
import {
    Alert,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AgreementCheckbox from '../../../ui/AgreementCheckbox';
import {PaymentOfferPopup} from '../PaymentOfferPopup/PaymentOfferPopup';
import {pluralize} from '../../../functionts/pluralize';
import PaymentPopup from '../PaymentPopup/PaymentPopup';
import instance from '../../../instance';
import {IGetBalanceInfo} from '../../../interfaces/fetch/balance';
import CallNotification from '../../../context/NotificationContext';
import {useNavigate} from 'react-router-dom';
import InvoicePopup from '../Invoice/InvoicePopup';
import {EServiceRent} from '../../../Enums/ServiceRent';
import ServiceRentIcon from '../../../ui/ServiceRentIcon';

interface IConfirmPayment {
    merchantListWithIncludedCash: any;
    totalCount: number;
    totalSum: number;
    userId: number | null;
    close: () => void;
}

const ACCESS_INVOICE_PAYMENT_MIN_SUM = 20300;
const ACCESS_INVOICE_PAYMENT_MIN_SUM_BASE_ONLY = 6090;

export const ConfirmPayment = (props: IConfirmPayment) => {
    const {merchantListWithIncludedCash, totalCount, totalSum, userId, close} = props;

    const [checkboxChecked, setCheckboxChecked] = React.useState(false);
    const [openDialog, setOpenDialog] = React.useState(false);
    const checkboxValue = useRef<HTMLInputElement | null>(null);
    const [paymentPopupOpen, setPaymentPopupOpen] = React.useState(false);
    const [invoicePopupOpen, setInvoicePopupOpen] = React.useState(false);
    const [isInvoicePayment, setIsInvoicePayment] = React.useState(false);
    const [paymentParams, setPaymentParams] = React.useState<{paySrc?: string; error?: string} | null>(null);
    const [orderId, setOrderId] = React.useState<string | null>(null);
    const [isPolling, setIsPolling] = React.useState(false);
    const [paymentStatusText, setPaymentStatusText] = React.useState<string | React.ReactNode | null>(null);
    const [balance, setBalance] = useState<number | null>(null);
    const {handleErrorNotification} = CallNotification();

    const navigate = useNavigate();

    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down('md'));

    // Касса на «Прокси» без «Номенклатуры» оплачивается только по счёту
    const isBaseOnly = (kkm: any) => !kkm.service_rent?.some((rent: any) => rent.service_rent_id === EServiceRent.Nomenclature);
    const cartKKMList: any[] = merchantListWithIncludedCash.flatMap((merchant: any) => merchant.kkm_list);
    const hasBaseOnlyKKM = cartKKMList.some(isBaseOnly);
    const isBaseOnlyCart = cartKKMList.length > 0 && cartKKMList.every(isBaseOnly);
    const invoiceMinSum = isBaseOnlyCart ? ACCESS_INVOICE_PAYMENT_MIN_SUM_BASE_ONLY : ACCESS_INVOICE_PAYMENT_MIN_SUM;
    const baseOnlyCount = cartKKMList.filter(isBaseOnly).length;
    const nomenclatureCount = cartKKMList.length - baseOnlyCount;
    const formatMoney = (value: number) => new Intl.NumberFormat('ru-RU').format(value);
    const kkmAmount = (kkm: any) =>
        kkm.quantity * (kkm.create_payment_info?.upcoming_payment_amount || kkm.create_payment_info?.payment_amount_by_rent || 0);
    const monthsMin = Math.min(...cartKKMList.map((kkm: any) => kkm.quantity));
    const monthsMax = Math.max(...cartKKMList.map((kkm: any) => kkm.quantity));
    const monthsLabel = monthsMin === monthsMax ? pluralize(monthsMax, 'месяц', 'месяца', 'месяцев') : `${monthsMin}–${monthsMax} месяцев`;

    // TODO Пока комментирую. Нужно только для оплаты с кошелька
    // const getBalanceInfo = async (userId: number) => {
    //     const request: IGetBalanceInfo = {
    //         method: 'GetBalanceInfo',
    //         params: {
    //             user_id: Number(userId),
    //         },
    //     };
    //     try {
    //         const response = await instance.post('/LK', request);
    //         handleErrorNotification(response);
    //         setBalance(response.data?.balance_value);
    //     } catch (error) {
    //         console.error('Error GetBalanceInfo:', error);
    //     }
    // };
    //
    // useEffect(() => {
    //     if (userId) {
    //         void getBalanceInfo(userId);
    //     }
    // }, [userId]);
    //
    // const handlePaymentFromBalance = async () => {
    //     const kkmIds = getKKMIdsForPayment();
    //
    //     if (!kkmIds.length) {
    //         console.error('Недостаточно данных для создания платежа');
    //         return;
    //     }
    //
    //     try {
    //         const response = await instance.post('/LK', {
    //             method: 'CreateCartBundlePayment',
    //             params: {
    //                 kkm_ids: kkmIds,
    //                 user_id: userId,
    //                 from_wallet: true,
    //                 quantity: quantity,
    //                 back_ref_url: window.location.origin + '/ok',
    //             },
    //         });
    //         handleErrorNotification(response);
    //
    //         const order = response.data?.order;
    //
    //         if (order) {
    //             // TODO Тут статус показать
    //         } else {
    //             console.error('Ошибка при списании с баланса');
    //         }
    //     } catch (error) {
    //         console.error('Ошибка при списании с баланса', error);
    //     }
    // };

    const checkboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCheckboxChecked(event.target.checked);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleClickOffer = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setOpenDialog(true);
    };

    const getItemsForPayment = () => cartKKMList.map((kkm: any) => ({kkm_id: kkm.kkm_id, quantity: kkm.quantity}));

    const handlePayment = async (isCreateInvoice: boolean = false) => {
        const items = getItemsForPayment();

        if (!items.length) {
            console.error('Недостаточно данных для создания платежа');
            return;
        }

        setPaymentParams(null);

        if (isCreateInvoice) {
            setInvoicePopupOpen(true);
            setIsInvoicePayment(true);
        } else {
            setPaymentPopupOpen(true);
        }

        try {
            const response = await instance.post('/LK', {
                method: 'CreateCartBundlePayment',
                params: {
                    items: items,
                    user_id: userId,
                    from_wallet: false,
                    back_ref_url: window.location.origin + '/ok',
                    create_invoice: isCreateInvoice,
                },
            });
            handleErrorNotification(response);

            const paySrc = response.data?.pay_src;
            const order = response.data?.order;

            if (isCreateInvoice) {
                setOrderId(order);
            } else if (paySrc) {
                setPaymentParams({paySrc});
                setOrderId(order);
            } else {
                console.error('Не удалось получить платёжную ссылку');
                setPaymentParams({error: 'Не удалось получить платёжную ссылку'});
            }
        } catch (error) {
            console.error('Ошибка при создании платежа', error);
            setPaymentParams({error: 'Ошибка при создании платежа'});
        }
    };

    const pollPaymentStatus = async (order: string) => {
        const maxAttempts = 10;
        const waitBetween = 3000;
        let attempts = 0;

        setIsPolling(true);
        setPaymentStatusText('Ожидаем подтверждения оплаты...');

        while (attempts < maxAttempts) {
            try {
                const response = await instance.post('/LK', {
                    method: 'GetPaymentInfo',
                    params: {order, user_id: userId},
                });
                handleErrorNotification(response);

                if (!response?.data?.error) {
                    const paymentSum: number = response.data?.payment_info?.total_amount;
                    const status: number = response.data?.payment_info?.payment_status;

                    const statusTextMap: Record<number, string> = {
                        0: 'Ожидаем подтверждения оплаты...',
                        1: `Платёж на сумму ${paymentSum} ₸ успешно принят`,
                        2: 'Платёж отклонён',
                        3: 'Платёж отменён',
                        5: 'Платёж выполняется',
                    };

                    const finalStatusText = statusTextMap[status] || 'Неизвестный статус платежа';
                    setPaymentStatusText(finalStatusText);

                    if (status !== 0) {
                        setIsPolling(false);
                        return;
                    }
                }
            } catch (e) {
                console.warn('Ошибка запроса GetPayment', e);
            }

            attempts++;
            await new Promise(resolve => setTimeout(resolve, waitBetween));
        }

        setIsPolling(false);
        setPaymentStatusText(
            <span style={{textAlign: 'center', display: 'inline-block'}}>
                Платёж ещё не обработан.
                <br />
                Вы можете проследить за его статусом в разделе{' '}
                <Link onClick={() => void navigate('/payment/history')} sx={{cursor: 'pointer'}}>
                    «История заказов»
                </Link>
            </span>,
        );
    };

    const handlePopupClose = () => {
        setPaymentPopupOpen(false);
        setInvoicePopupOpen(false);
        if (orderId && !isInvoicePayment) {
            void pollPaymentStatus(orderId);
        } else if (isInvoicePayment) {
            setPaymentStatusText(
                <span style={{textAlign: 'center', display: 'inline-block'}}>
                    Ожидаем оплаты по счёту.
                    <br />
                    Вы можете проследить за статусом платежа в разделе{' '}
                    <Link onClick={() => void navigate('/payment/history')} sx={{cursor: 'pointer'}}>
                        «История заказов»
                    </Link>
                </span>,
            );
        }
    };

    const renderLegalEntities = (entities: any[]) => (
        <>
            {entities.map((entity: any, idx: number) => (
                <Box key={idx} mb={3}>
                    <Typography fontWeight="bold" gutterBottom>
                        {entity.name} (ИИН/БИН: {entity.inn})
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        Кол-во касс: {entity.payment_cash_count} | Сумма: {entity.payment_cash_sum} ₸
                    </Typography>

                    <Box sx={{overflowX: 'auto'}}>
                        <Table
                            size="small"
                            sx={{
                                minWidth: isSm ? 900 : 0,
                                '& td, & th': {whiteSpace: 'nowrap'},
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>Модель</TableCell>
                                    <TableCell>Рег. номер</TableCell>
                                    <TableCell>Сер. номер</TableCell>
                                    <TableCell>ОФД</TableCell>
                                    <TableCell>Место торговли</TableCell>
                                    <TableCell>Тарифы</TableCell>
                                    <TableCell sx={{width: 47}}>Кол-во</TableCell>
                                    <TableCell>Сумма</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {entity.kkm_list?.map((kkm: any, kkmIdx: number) => (
                                    <TableRow key={kkmIdx} sx={{'&:last-child td, &:last-child th': {borderBottom: 0}}}>
                                        <TableCell>{kkm.model}</TableCell>
                                        <TableCell>{kkm.reg_number}</TableCell>
                                        <TableCell>{kkm.serial_number}</TableCell>
                                        <TableCell>{kkm?.ofd?.name}</TableCell>
                                        <TableCell>
                                            {kkm.department?.name || '-'} / {kkm.department?.address || '-'}
                                        </TableCell>
                                        <TableCell>
                                            <ServiceRentIcon kkm={kkm} />
                                        </TableCell>
                                        <TableCell>{kkm.quantity}</TableCell>
                                        <TableCell>{kkmAmount(kkm)} ₸</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>
            ))}
        </>
    );

    if (isPolling || paymentStatusText) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="300px">
                <Typography variant="h6" mb={2}>
                    {paymentStatusText}
                </Typography>
                {isInvoicePayment ? (
                    <Typography mb={2} mt={5} color={'text.secondary'}>
                        <InfoOutlinedIcon sx={{mb: '-6px', mr: 1}} color={'disabled'} />
                        Обработка платежа по счёту может занять несколько рабочих дней.
                    </Typography>
                ) : (
                    <></>
                )}
            </Box>
        );
    }

    const invoiceMinSumHint = totalSum < invoiceMinSum && (
        <Box display="flex" alignItems="center" gap={0.5}>
            <InfoOutlinedIcon fontSize="small" color="warning" />
            <Typography variant="caption" color="text.secondary" sx={{lineHeight: '20px'}}>
                Минимальная сумма счёта — {formatMoney(invoiceMinSum)} ₸
            </Typography>
        </Box>
    );

    return (
        <>
            <Typography variant="h4" gutterBottom>
                Подтверждение оплаты
            </Typography>

            <Box mb={isSm ? 2 : 3}>
                <Accordion disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Посмотреть список касс, включённых в оплату ({totalCount})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>{renderLegalEntities(merchantListWithIncludedCash)}</AccordionDetails>
                </Accordion>
            </Box>

            <Paper>
                <Box p={{xs: 2, sm: 4, md: 5}}>
                    <Box mb={{xs: 2, sm: 4}}>
                        <Typography variant="h6">
                            Сумма к оплате: <strong>{formatMoney(totalSum)} ₸</strong>
                        </Typography>
                        <Typography variant="body1">
                            По {pluralize(totalCount, 'кассе', 'кассам', 'кассам')} за {monthsLabel}.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {baseOnlyCount > 0 && `Прокси × ${baseOnlyCount}`}
                            {baseOnlyCount > 0 && nomenclatureCount > 0 && ' · '}
                            {nomenclatureCount > 0 && `Прокси + Номенклатура × ${nomenclatureCount}`}
                        </Typography>
                        {hasBaseOnlyKKM && (
                            <Alert severity="info" sx={{mt: 1.5}}>
                                <Typography variant="body2">
                                    В заказе есть кассы на тарифе «Прокси» без «Номенклатуры» – такие кассы оплачиваются только по счёту, оплата
                                    картой недоступна.{' '}
                                    {isBaseOnlyCart
                                        ? `Минимальная сумма счёта – ${formatMoney(ACCESS_INVOICE_PAYMENT_MIN_SUM_BASE_ONLY)} ₸.`
                                        : `Для заказа с кассами «Прокси + Номенклатура» минимальная сумма счёта – ${formatMoney(ACCESS_INVOICE_PAYMENT_MIN_SUM)} ₸. Чтобы выставить счёт от ${formatMoney(ACCESS_INVOICE_PAYMENT_MIN_SUM_BASE_ONLY)} ₸, оставьте в заказе только кассы на «Прокси», а кассы «Прокси + Номенклатура» оплатите отдельно – картой или по счёту.`}
                                </Typography>
                            </Alert>
                        )}
                    </Box>

                    <AgreementCheckbox
                        checkboxChecked={checkboxChecked}
                        checkboxChange={checkboxChange}
                        checkboxValue={checkboxValue}
                        label={
                            <>
                                Я соглашаюсь, что оплаченная мной услуга считается оказанной в полном объёме, и, согласно договору{' '}
                                <span
                                    style={{
                                        color: '#0063FF',
                                        textDecoration: 'underline dotted',
                                        cursor: 'pointer',
                                    }}
                                    onClick={handleClickOffer}
                                >
                                    Оферты
                                </span>
                                , перерасчёту, возврату или обмену не подлежит.
                            </>
                        }
                    />

                    <Box mt={hasBaseOnlyKKM ? 2 : 3} display="grid" gridTemplateColumns={{xs: '1fr', sm: '1fr 1fr'}} gap={isSm ? 2 : 3}>
                        {!hasBaseOnlyKKM && (
                            <Paper variant="outlined" sx={{p: 3}}>
                                <Box display="flex" alignItems="center" gap={2} mb={2}>
                                    <CreditCardIcon color="primary" fontSize="large" />
                                    <Box>
                                        <Typography variant="h6">Оплата картой</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Мгновенное пополнение через банковскую карту
                                        </Typography>
                                    </Box>
                                </Box>
                                <Button variant="contained" disabled={!checkboxChecked} onClick={() => void handlePayment()}>
                                    Оплатить картой
                                </Button>
                            </Paper>
                        )}

                        <Paper variant="outlined" sx={{p: 3}}>
                            <Box display="flex" alignItems="center" gap={2} mb={2}>
                                <DescriptionIcon color="primary" fontSize="large" />
                                <Box>
                                    <Typography variant="h6">Счёт на оплату</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Для безналичной оплаты по реквизитам
                                    </Typography>
                                </Box>
                            </Box>
                            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                                <Button
                                    variant="contained"
                                    disabled={!checkboxChecked || totalSum < invoiceMinSum}
                                    onClick={() => void handlePayment(true)}
                                >
                                    Сформировать счёт
                                </Button>
                                {invoiceMinSumHint}
                            </Box>
                        </Paper>
                    </Box>
                </Box>
            </Paper>

            <Box mt={3}>
                <Link onClick={close} underline="hover" sx={{cursor: 'pointer'}}>
                    Вернуться к выбору услуг для оплаты
                </Link>
            </Box>

            <PaymentPopup open={paymentPopupOpen} setOpen={setPaymentPopupOpen} paymentParams={paymentParams} onClose={handlePopupClose} />
            <PaymentOfferPopup openDialog={openDialog} handleCloseDialog={handleCloseDialog} />
            <InvoicePopup open={invoicePopupOpen} setOpen={setInvoicePopupOpen} orderId={orderId} userId={userId} onClose={handlePopupClose} />
        </>
    );
};
